// src/client/renderer/vfx/env/shooting-stars-pool.ts
//
// Plan 18, Task 2.1 — Shooting Stars sky-dome sporadic ambient pool.
//
// Behaviour contract:
//   - AMBIENT-mode SPORADIC spawner. mode: 'ambient'.
//   - Pool's internal logic spawns ~one star every 8–15s (random interval per
//     emitter); the wire-up site calls manager.spawn('shooting_stars', {}) once
//     at scene-build time to ENABLE the spawner.
//   - Each star is a thin elongated PlaneGeometry (~1–2 units long) with
//     additive material (white 0xffffff, opacity 0→1→0). Born at a random
//     position on the sky-dome inner-surface, traverses ~30 units along a
//     random tangent vector over ~1.4 seconds, then despawns.
//   - Internal state: spawner accumulator + currently active stars (max = 3).
//   - update(dt) advances the accumulator AND each active star's traversal.
//   - isFinished(star) returns true when star elapsed > TTL.
//
// Design notes:
//   - The "spawner handle" returned by acquire() is a THREE.Group used as the
//     stable emitter token for the manager. It is added to scene on acquire and
//     removed on release/dispose.
//   - Each star is a THREE.Mesh with a PER-STAR geometry (small PlaneGeometry)
//     and uses the SHARED MeshBasicMaterial (tinted white, additive). The
//     per-star opacity is tracked via a proxy object driven by the gsap timeline;
//     a MeshBasicMaterial clone is created per star so each star has its own
//     opacity channel. The shared geometry is disposed once in dispose().
//   - The gsap registry is used for the star traversal + opacity tween.
//     tl.call(fn, undefined, absolutePos) is used for completion (NEVER
//     eventCallback('onComplete') — gsap-registry VIRTUAL_DURATION sentinel
//     breaks that path).
//   - Deterministic RNG is injected via optional `seed` parameter (defaults to
//     a fixed value for reproducibility in tests). The pool factory accepts an
//     extended deps bag with an optional `seed?: number` field.
//   - Stars whose isFinished==true are cleaned up at the next update() tick
//     (lazy removal to avoid mutating the active set during iteration).

import * as THREE from 'three';
import type {
  EnvCeremonyPool,
  EnvCeremonyPoolDeps,
  EnvCeremonySpawnArgs,
} from './env-ceremony-types';
import type { GsapRegistry } from '../gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Extended deps (optional seed injection — does NOT modify EnvCeremonyPoolDeps)
// ─────────────────────────────────────────────────────────────────────────────

interface ShootingStarsPoolConfig extends EnvCeremonyPoolDeps {
  readonly seed?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Sky-dome radius — stars are born on the inner surface. */
const SKY_RADIUS = 95;

/** Star traversal distance (units). */
const STAR_TRAVERSE = 30;

/** Star lifetime (seconds): traversal animation duration. */
const STAR_TTL = 1.4;

/** Opacity ramp-up / ramp-down duration each (half of TTL). */
const STAR_FADE = STAR_TTL / 2;

/** Star geometry dimensions. */
const STAR_WIDTH = 0.5;
const STAR_HEIGHT = 1.8;

/** Star material colour: white. */
const STAR_COLOUR = 0xffffff;

/** Maximum simultaneously active stars. */
const MAX_ACTIVE = 3;

/** Minimum spawn interval (seconds). */
const SPAWN_INTERVAL_MIN = 8.0;

/** Maximum spawn interval (seconds). */
const SPAWN_INTERVAL_MAX = 15.0;

// ─────────────────────────────────────────────────────────────────────────────
// LCG RNG (deterministic)
// ─────────────────────────────────────────────────────────────────────────────

function makeLcg(seed: number): () => number {
  let state = seed;
  return function next(): number {
    state = (state * 9301 + 49297) % 233280;
    return state / 233280;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-star state
// ─────────────────────────────────────────────────────────────────────────────

interface StarState {
  mesh: THREE.Mesh;
  elapsed: number;
  ttl: number;
  finished: boolean;
  tlHandle: ReturnType<GsapRegistry['create']>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createShootingStarsPool({
  scene,
  registry,
  seed = 8675309,
}: ShootingStarsPoolConfig): EnvCeremonyPool {
  const rng = makeLcg(seed);

  /** Shared geometry — disposed once in dispose(). */
  const sharedGeometry = new THREE.PlaneGeometry(STAR_WIDTH, STAR_HEIGHT);

  /** Active star states. Keys are THREE.Mesh instances (star meshes), stored as Object3D for public API compat. */
  const activeStars = new Map<THREE.Object3D, StarState>();

  /** Spawner handles (one per acquire() call). */
  const spawnerHandles = new Set<THREE.Object3D>();

  /** Per-spawner accumulator (time until next star spawn). */
  const spawnerAccumulators = new Map<THREE.Object3D, number>();

  let disposed = false;

  // ─────────────────────────────────────────────────────────────────────────
  // Internal: spawn a new star
  // ─────────────────────────────────────────────────────────────────────────

  function spawnStar(): void {
    if (activeStars.size >= MAX_ACTIVE) return;

    // Random position on the sky-dome inner surface (spherical coords)
    const theta = rng() * Math.PI; // polar angle [0, π]
    const phi = rng() * Math.PI * 2; // azimuthal angle [0, 2π]
    const startX = SKY_RADIUS * Math.sin(theta) * Math.cos(phi);
    const startY = SKY_RADIUS * Math.cos(theta);
    const startZ = SKY_RADIUS * Math.sin(theta) * Math.sin(phi);

    // Random tangent traversal direction (perpendicular to radial vector)
    const radial = new THREE.Vector3(startX, startY, startZ).normalize();
    // Pick a random axis not parallel to radial
    const arbitrary =
      Math.abs(radial.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
    const tangent = new THREE.Vector3().crossVectors(radial, arbitrary).normalize();

    const endX = startX + tangent.x * STAR_TRAVERSE;
    const endY = startY + tangent.y * STAR_TRAVERSE;
    const endZ = startZ + tangent.z * STAR_TRAVERSE;

    // Per-star material clone (own opacity channel)
    const material = new THREE.MeshBasicMaterial({
      color: STAR_COLOUR,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(sharedGeometry, material);
    mesh.position.set(startX, startY, startZ);
    scene.add(mesh);

    const tlHandle = registry.create('shooting_star');
    const tl = tlHandle.timeline;

    // Opacity 0 → 1 (ramp up)
    const opProxy = { value: 0.0 };
    tl.to(
      opProxy,
      {
        value: 1.0,
        duration: STAR_FADE,
        ease: 'power1.in',
        onUpdate: () => {
          material.opacity = opProxy.value;
        },
      },
      0,
    );

    // Opacity 1 → 0 (ramp down)
    tl.to(
      opProxy,
      {
        value: 0.0,
        duration: STAR_FADE,
        ease: 'power1.out',
        onUpdate: () => {
          material.opacity = opProxy.value;
        },
      },
      STAR_FADE,
    );

    // Position traversal (parallel with opacity)
    tl.to(
      mesh.position,
      {
        x: endX,
        y: endY,
        z: endZ,
        duration: STAR_TTL,
        ease: 'none',
      },
      0,
    );

    // Mark finished via tl.call at TTL position (NEVER eventCallback)
    const starState: StarState = {
      mesh,
      elapsed: 0,
      ttl: STAR_TTL,
      finished: false,
      tlHandle,
    };

    tl.call(
      () => {
        starState.finished = true;
      },
      undefined,
      STAR_TTL,
    );

    activeStars.set(mesh, starState);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Internal: initialize a fresh accumulator for a spawner handle
  // ─────────────────────────────────────────────────────────────────────────

  function nextInterval(): number {
    return SPAWN_INTERVAL_MIN + rng() * (SPAWN_INTERVAL_MAX - SPAWN_INTERVAL_MIN);
  }

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(_args: EnvCeremonySpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    const spawnerGroup = new THREE.Group();
    scene.add(spawnerGroup);
    spawnerHandles.add(spawnerGroup);
    spawnerAccumulators.set(spawnerGroup, nextInterval());
    return spawnerGroup;
  }

  // ── update ─────────────────────────────────────────────────────────────────

  function update(deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    if (disposed) return new Set();

    // Advance spawner accumulators; spawn stars when accumulator expires
    for (const [handle, acc] of spawnerAccumulators) {
      const newAcc = acc - deltaSeconds;
      if (newAcc <= 0) {
        spawnStar();
        spawnerAccumulators.set(handle, nextInterval());
      } else {
        spawnerAccumulators.set(handle, newAcc);
      }
    }

    // Advance active stars and advance their gsap timelines
    const toRemove: THREE.Object3D[] = [];
    for (const [mesh, state] of activeStars) {
      state.elapsed += deltaSeconds;
      // Advance this star's gsap timeline manually (drives position + opacity tweens)
      state.tlHandle.timeline.time(state.elapsed);
      // Mark finished by elapsed-time comparison (don't rely solely on tl.call
      // since registry.update() is driven externally by the manager)
      if (state.elapsed >= state.ttl || state.finished) {
        state.finished = true;
        toRemove.push(mesh);
      }
    }

    // Clean up finished stars
    for (const mesh of toRemove) {
      const state = activeStars.get(mesh);
      if (state) {
        state.tlHandle.dispose();
        scene.remove(mesh);
        ((mesh as THREE.Mesh).material as THREE.MeshBasicMaterial).dispose();
        activeStars.delete(mesh);
      }
    }

    // Build active set: spawner handles + currently-active stars
    const active = new Set<THREE.Object3D>();
    for (const handle of spawnerHandles) {
      active.add(handle);
    }
    for (const mesh of activeStars.keys()) {
      active.add(mesh);
    }
    return active;
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    // Spawner handles are always-on (never finished)
    if (spawnerHandles.has(emitter)) return false;

    // Star emitters: finished when elapsed > TTL
    const state = activeStars.get(emitter);
    if (state) return state.finished;

    // Unknown emitter — treat as finished (safe default)
    return true;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    if (spawnerHandles.has(emitter)) {
      scene.remove(emitter);
      spawnerHandles.delete(emitter);
      spawnerAccumulators.delete(emitter);
      return;
    }

    const state = activeStars.get(emitter);
    if (state) {
      state.tlHandle.dispose();
      scene.remove(emitter);
      const mesh = emitter as THREE.Mesh;
      if (mesh.material) {
        (mesh.material as THREE.MeshBasicMaterial).dispose();
      }
      activeStars.delete(emitter);
    }
  }

  // ── dispose (F4) ───────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Dispose all active star emitters
    for (const [obj, state] of activeStars) {
      state.tlHandle.dispose();
      scene.remove(obj);
      ((obj as THREE.Mesh).material as THREE.MeshBasicMaterial).dispose();
    }
    activeStars.clear();

    // Remove spawner handles
    for (const handle of spawnerHandles) {
      scene.remove(handle);
    }
    spawnerHandles.clear();
    spawnerAccumulators.clear();

    // Dispose shared geometry exactly once
    sharedGeometry.dispose();
  }

  return { mode: 'ambient', acquire, update, isFinished, release, dispose };
}
